import '@/i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import { router } from '@/routes'
import { IntercomInitializer, LoadDeviceInfos, SessionInitializer, StartParamRouter, TelegramSessionInitializer } from '@/components/app-effects'
import { AuthDebugPanel } from '@/components/__debug__/AuthDebugPanel'
import { StartParamDebugPanel } from '@/components/__debug__/StartParamDebugPanel'
import { useRegisterAuthListener } from '@/hooks/useRegisterAuthListener'
import { useLanguageSync } from '@/hooks/useLanguageSync'
import { useUserStore } from '@/store/useUserStore'
import { useEffect } from 'react'
import TgProvider from '@/services/providers/TgProvider'
import OffsetsProvider from '@/services/providers/OffsetsProvider'
import ToasterProvider from '@/services/providers/ToasterProvider'
import config from '@/config/env'
import eruda from 'eruda'
import { initializeOpenAI } from '@/services/openai'

const queryClient = new QueryClient()

const envValue = (config.ENV || '').toString().trim().toLowerCase()
const isProduction = envValue === 'production'

if (!isProduction) {
  eruda.init({ tool: ['console', 'elements', 'network'] })
}

// Initialize Sentry only in production
if (isProduction) {
  import('@sentry/react').then((SentryModule) => {
    SentryModule.init({
      dsn: 'https://c4817a609806d5b6a238b2365bc06255@o4510154819108864.ingest.de.sentry.io/4510154821206096',
      environment: config.ENV || 'production',
      sendDefaultPii: true,
      beforeSend(event: any, hint: any) {
        // Block errors from ad blockers
        if (
          hint.originalException?.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
          hint.originalException?.message?.includes('NetworkError') ||
          hint.originalException?.message?.includes('Failed to fetch')
        ) {
          return null
        }
        return event
      },
      integrations: [],
      tracesSampleRate: 0.1,
    })
  })
}

const App = () => {
  const initializeMockWallet = useUserStore(
    (state) => state.initializeMockWallet,
  )
  const wallet = useUserStore((state) => state.wallet)

  // Register authentication event listener
  useRegisterAuthListener()

  // Sync language with settings
  useLanguageSync()

  // Initialize mock wallet data for testing
  useEffect(() => {
    if (!wallet) {
      initializeMockWallet()
    }
  }, [wallet, initializeMockWallet])

  // Initialize OpenAI service
  useEffect(() => {
    if (config.VITE_OPENAI_KEY) {
      initializeOpenAI(config.VITE_OPENAI_KEY)
    } else {
      console.warn('OpenAI API key not found. AI features will not work.')
    }
  }, [])

  return (
    <OffsetsProvider>
      <TgProvider>
        <ToasterProvider>
          <QueryClientProvider client={queryClient}>
            <IntercomInitializer />
            <LoadDeviceInfos />
            <SessionInitializer />
            <TelegramSessionInitializer />
            <StartParamRouter />
            <RouterProvider router={router} />
            <AuthDebugPanel />
            <StartParamDebugPanel />
          </QueryClientProvider>
        </ToasterProvider>
      </TgProvider>
    </OffsetsProvider>
  )
}

export default App
